/**
 * **Verbindungs-Vault** — die eine Stelle, an der Zugangsdaten zu fremden
 * Diensten liegen (ADR 0017).
 *
 * Vorher hatte jede Einbaustelle ihr eigenes Credential-Formular: Versand je
 * Queue, IMAP je Queue, Systemmail auf Plattform-Ebene, KI je Mandant,
 * SkillLock je Mandant. Denselben Scaleway-Schluessel musste man dreimal
 * eintippen. Jetzt gilt: **eine Verbindung, viele Referenzen.**
 *
 * Verbindungen gehoeren der **Plattform** (wie `users` und `platform_settings`)
 * und werden je Mandant **freigegeben** (`connection_grants`). Ein
 * Mandanten-Admin sieht nur Name, Typ und Provider einer freigegebenen
 * Verbindung — nie ein Geheimnis, auch nicht verschluesselt.
 *
 * Dieselben drei Schema-Ebenen wie bei `email.ts` und `platform.ts`:
 *
 *   - **gespeichert** (`connectionConfigSchema`) — genau das, was in
 *     `connections.config` liegt, mit den Feldern `…_encrypted` (libsodium
 *     sealed box, `services/credentials.ts`).
 *   - **Eingabe** (`connectionConfigInputSchema`) — Klartext, **write-only**:
 *     ein weggelassenes Geheimnis heisst „unveraendert lassen".
 *   - **oeffentlich** (`connectionConfigPublicSchema`) — statt des
 *     Geheimnisses nur `has_api_key` / `has_secret_key` / `has_password`.
 *
 * `redactConnectionConfig()` ist die einzige Bruecke von „gespeichert" nach
 * „oeffentlich" — damit kann kein Endpunkt versehentlich eins ausliefern.
 *
 * Doku: docs/services/connections.md
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
import { aiProviderSchema } from './ai.js';
import { hostnameSchema, outboundMailProviderSchema, scalewayRegionSchema, IMAP_PORT_DEFAULT, } from './email.js';
/**
 * Die vier Sorten Zugang, die dieses System kennt. Bewusst geschlossen: eine
 * fuenfte Sorte ist eine Entscheidung, kein Konfigurationswert.
 */
export const CONNECTION_TYPES = [
    'mail_provider',
    'imap_account',
    'ai_provider',
    'skilllock',
];
export const connectionTypeSchema = z.enum(CONNECTION_TYPES);
// ─────────────────────────────────────────── mail_provider (Spec 6.4) ───────
/**
 * Der Versand-Zugang. Klartext-Kennungen (`access_key`, `project_id`,
 * `region`) stehen offen — sie sind keine Geheimnisse und machen beim Rotieren
 * erkennbar, welches Schluesselpaar hinterlegt ist (ADR 0013).
 *
 * `domains` ist die **von Hand gepflegte** Liste der beim Provider
 * verifizierten Absender-Domains. Sie ist optional: ist sie leer, prueft
 * nichts, und eine Absenderadresse gilt als moeglich. Ist sie gefuellt, warnt
 * die Oberflaeche bei einer Adresse ausserhalb dieser Domains — eine
 * Live-Abfrage beim Provider gibt es bewusst nicht (jeder Provider
 * beantwortet sie anders, und ein Mensch pflegt die Domains ohnehin dort).
 */
const mailConnectionBase = z.object({
    type: z.literal('mail_provider'),
    provider: outboundMailProviderSchema,
    access_key: z.string().min(1).max(64).nullable().default(null),
    project_id: z.string().min(1).max(64).nullable().default(null),
    region: scalewayRegionSchema.nullable().default(null),
    domains: z.array(hostnameSchema).max(20).default([]),
});
const mailConnectionConfigSchema = mailConnectionBase.extend({
    api_key_encrypted: z.string().nullable().default(null),
    secret_key_encrypted: z.string().nullable().default(null),
});
const mailConnectionInputSchema = mailConnectionBase.extend({
    api_key: z.string().min(1).max(512).optional(),
    secret_key: z.string().min(1).max(512).optional(),
});
const mailConnectionPublicSchema = mailConnectionBase.extend({
    has_api_key: z.boolean(),
    has_secret_key: z.boolean(),
});
// ───────────────────────────────────────── imap_account (Spec 6.1.2) ────────
/**
 * Das Postfach selbst — Host, Benutzer, Passwort. **Ordner und Poll-Intervall
 * stehen nicht hier**, sondern an der Queue (`queues.inbound_config`): zwei
 * Queues duerfen dasselbe Konto mit verschiedenen Ordnern benutzen.
 */
const imapConnectionBase = z.object({
    type: z.literal('imap_account'),
    host: z.string().min(1).max(253),
    port: z.number().int().min(1).max(65535).default(IMAP_PORT_DEFAULT),
    user: z.string().min(1).max(320),
    /** `true` = implizites TLS (993), `false` = STARTTLS-Upgrade (143). */
    secure: z.boolean().default(true),
});
const imapConnectionConfigSchema = imapConnectionBase.extend({
    password_encrypted: z.string().nullable().default(null),
});
const imapConnectionInputSchema = imapConnectionBase.extend({
    password: z.string().min(1).max(512).optional(),
});
const imapConnectionPublicSchema = imapConnectionBase.extend({
    has_password: z.boolean(),
});
// ─────────────────────────────────────────── ai_provider (Spec 9.1) ─────────
/**
 * Der KI-Zugang. `embedding_api_key` ist der zweite Schluessel aus ADR 0009:
 * Anthropic hat keine Embedding-API, ein Mandant auf Anthropic braucht dafuer
 * einen OpenAI-Schluessel.
 */
const aiConnectionBase = z.object({
    type: z.literal('ai_provider'),
    provider: aiProviderSchema,
    model: z.string().min(1).max(200),
    embedding_model: z.string().min(1).max(200),
});
const aiConnectionConfigSchema = aiConnectionBase.extend({
    api_key_encrypted: z.string().nullable().default(null),
    embedding_api_key_encrypted: z.string().nullable().default(null),
});
const aiConnectionInputSchema = aiConnectionBase.extend({
    api_key: z.string().min(8).max(512).optional(),
    embedding_api_key: z.string().min(8).max(512).optional(),
});
const aiConnectionPublicSchema = aiConnectionBase.extend({
    has_api_key: z.boolean(),
    has_embedding_api_key: z.boolean(),
});
// ────────────────────────────────────────────── skilllock (Spec 10) ─────────
/**
 * Die SkillLock-Anbindung. `webhook_secret` ist der HMAC-Schluessel des
 * **eingehenden** Status-Webhooks; ueber ihn findet der Endpunkt seinen
 * Mandanten (ADR 0012).
 */
const skilllockConnectionBase = z.object({
    type: z.literal('skilllock'),
    base_url: z.url().max(500),
    /** Board/Projekt, in das „Task anlegen" ohne eigene Angabe schreibt. */
    default_board_id: z.string().max(200).nullable().default(null),
});
const skilllockConnectionConfigSchema = skilllockConnectionBase.extend({
    api_key_encrypted: z.string().nullable().default(null),
    webhook_secret_encrypted: z.string().nullable().default(null),
});
const skilllockConnectionInputSchema = skilllockConnectionBase.extend({
    api_key: z.string().min(8).max(512).optional(),
    webhook_secret: z.string().min(16).max(512).optional(),
});
const skilllockConnectionPublicSchema = skilllockConnectionBase.extend({
    has_api_key: z.boolean(),
    has_webhook_secret: z.boolean(),
});
// ─────────────────────────────────────────────────── Vereinigung ────────────
export const connectionConfigSchema = z.discriminatedUnion('type', [
    mailConnectionConfigSchema,
    imapConnectionConfigSchema,
    aiConnectionConfigSchema,
    skilllockConnectionConfigSchema,
]);
export const connectionConfigInputSchema = z.discriminatedUnion('type', [
    mailConnectionInputSchema,
    imapConnectionInputSchema,
    aiConnectionInputSchema,
    skilllockConnectionInputSchema,
]);
export const connectionConfigPublicSchema = z.discriminatedUnion('type', [
    mailConnectionPublicSchema,
    imapConnectionPublicSchema,
    aiConnectionPublicSchema,
    skilllockConnectionPublicSchema,
]);
/**
 * **Die einzige Bruecke** von „gespeichert" nach „oeffentlich". Jedes
 * `…_encrypted` wird durch ein `has_…` ersetzt; der Chiffretext verlaesst den
 * Server nie — auch nicht gegenueber einem Plattform-Admin.
 *
 * `?? null` statt `!== null`: Drizzle liest die jsonb-Spalte **ohne** Zod, die
 * `.default(null)` greifen also nur beim Schreiben (gleicher Grund wie in
 * `redactOutboundConfig()`, siehe `email.ts`).
 */
export function redactConnectionConfig(config) {
    switch (config.type) {
        case 'mail_provider': {
            const { api_key_encrypted: apiKey, secret_key_encrypted: secretKey, ...rest } = config;
            return {
                ...rest,
                access_key: rest.access_key ?? null,
                project_id: rest.project_id ?? null,
                region: rest.region ?? null,
                domains: rest.domains ?? [],
                has_api_key: (apiKey ?? null) !== null,
                has_secret_key: (secretKey ?? null) !== null,
            };
        }
        case 'imap_account': {
            const { password_encrypted: password, ...rest } = config;
            return { ...rest, has_password: (password ?? null) !== null };
        }
        case 'ai_provider': {
            const { api_key_encrypted: apiKey, embedding_api_key_encrypted: embeddingKey, ...rest } = config;
            return {
                ...rest,
                has_api_key: (apiKey ?? null) !== null,
                has_embedding_api_key: (embeddingKey ?? null) !== null,
            };
        }
        case 'skilllock': {
            const { api_key_encrypted: apiKey, webhook_secret_encrypted: webhookSecret, ...rest } = config;
            return {
                ...rest,
                default_board_id: rest.default_board_id ?? null,
                has_api_key: (apiKey ?? null) !== null,
                has_webhook_secret: (webhookSecret ?? null) !== null,
            };
        }
    }
}
/**
 * Der Provider-Schluessel einer Verbindung — die eine Stelle, die ihn aus der
 * Konfiguration ableitet. Er steht zusaetzlich als eigene Spalte in
 * `connections.provider`, damit sich Listen ohne jsonb-Ausdruecke filtern
 * lassen; geschrieben wird er immer von hier.
 */
export function providerOfConnection(config) {
    switch (config.type) {
        case 'mail_provider':
        case 'ai_provider':
            return config.provider;
        case 'imap_account':
            return 'imap';
        case 'skilllock':
            return 'skilllock';
    }
}
// ────────────────────────────────────────────────────── Datensaetze ─────────
/**
 * Verbindung in der Betreiber-Sicht („Plattform → Verbindungen").
 *
 * `granted_tenant_ids` sind die Freigaben, `usage_count` die Zahl der
 * Referenzen (Queues, Mandanten-Defaults, Systemmail) — er ist der Grund,
 * warum ein Loeschen scheitert.
 */
export const connectionSchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(200),
    type: connectionTypeSchema,
    provider: z.string().min(1).max(50),
    config: connectionConfigPublicSchema,
    granted_tenant_ids: z.array(uuidSchema),
    usage_count: z.number().int().nonnegative(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
/**
 * Was ein **Mandanten**-Admin von einer freigegebenen Verbindung sieht: genug,
 * um sie in einem Dropdown zu erkennen — und nicht mehr. Kein `has_api_key`,
 * kein Host, kein Modell: das ist Betreiber-Wissen.
 *
 * `domains` ist die Ausnahme und Absicht: die Absender-Validierung im
 * Mandanten-UI braucht sie, und eine verifizierte Domain ist oeffentlich
 * (sie steht im DNS).
 */
export const connectionRefSchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(200),
    type: connectionTypeSchema,
    provider: z.string().min(1).max(50),
    domains: z.array(z.string()).default([]),
});
/** Wo eine Verbindung benutzt wird — die Liste im Loesch-Fehler und im UI. */
export const CONNECTION_USAGE_KINDS = [
    'queue_outbound',
    'queue_inbound',
    'tenant_default_mail',
    'tenant_ai',
    'tenant_skilllock',
    'platform_system_mail',
];
export const connectionUsageKindSchema = z.enum(CONNECTION_USAGE_KINDS);
export const connectionUsageSchema = z.object({
    kind: connectionUsageKindSchema,
    /** `null` bei der Systemmail — die gehoert keinem Mandanten. */
    tenant_id: uuidSchema.nullable(),
    tenant_name: z.string().nullable(),
    /** Sprechender Name des benutzenden Objekts (Queue- bzw. Mandantenname). */
    label: z.string(),
});
export const connectionDetailSchema = connectionSchema.extend({
    usage: z.array(connectionUsageSchema),
});
export const createConnectionSchema = z.object({
    name: z.string().min(1).max(200),
    config: connectionConfigInputSchema,
    /** Freigaben gleich mit anlegen; leer = noch fuer niemanden sichtbar. */
    tenant_ids: z.array(uuidSchema).max(200).default([]),
});
/**
 * Aendern. Ein weggelassenes Feld bleibt; ein weggelassenes Geheimnis
 * ebenfalls. Der **Typ** einer Verbindung laesst sich nicht wechseln — das
 * waere eine andere Verbindung, und die Referenzen zeigten ins Leere.
 */
export const updateConnectionSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    config: connectionConfigInputSchema.optional(),
    tenant_ids: z.array(uuidSchema).max(200).optional(),
});
/**
 * Ergebnis des Test-Knopfs. `ok` ist immer `true` — ein Fehlschlag kommt als
 * Fehler-Envelope (`ExternalError` → 503), nicht als `ok: false`.
 */
export const connectionTestResultSchema = z.object({
    ok: z.literal(true),
    type: connectionTypeSchema,
    /** Eine Zeile Klartext fuer den Erfolgs-Toast, z. B. „INBOX: 12 Nachrichten". */
    detail: z.string(),
});
/**
 * Passt eine Absenderadresse zu den verifizierten Domains einer Verbindung?
 *
 * Reine Funktion, weil beide Seiten sie brauchen: das UI fuer den Hinweis am
 * Feld und der Server beim Speichern. Eine leere Domainliste heisst „nicht
 * gepflegt" — dann gibt es nichts zu pruefen und die Antwort ist `true`.
 */
export function fromAddressMatchesDomains(fromAddress, domains) {
    if (domains.length === 0)
        return true;
    const domain = fromAddress.split('@')[1]?.toLowerCase();
    if (domain === undefined)
        return false;
    return domains.some((entry) => {
        const known = entry.toLowerCase();
        return domain === known || domain.endsWith(`.${known}`);
    });
}
//# sourceMappingURL=connection.js.map